import { MultiTextComponent } from "@/components/Atoms/MultiText";
import styles from "./index.module.scss";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import { CustomUnderline, Underline } from "@/components/Atoms/Underline";
import { VariantProps, cva } from "class-variance-authority";
import { Shape } from "@/components/Atoms/Spacer";
import Image from "@/components/Atoms/Image";
import { getSectionMarginClasses } from "@/lib/section-margins";

export type SectionTextVariantProps = VariantProps<typeof SectionTextVariants>;

const SectionTextVariants = cva(``, {
  variants: {
    textColumnDisplay: {
      default: "items-start",
      center: "items-center",
      right: "items-end",
    },
    layout: {
      fullWidth: "",
      container: "max-w-[1360px] m-auto",
      smallContainer: "mx-auto w-full max-w-[960px]",
    },
    textAlignment: {
      center: "lg:items-center",
      fixedCenter: "items-center",
      left: "items-start",
      rigth: "items-end",
    },
    textColumnLayout: {
      single: "",
      double: "",
    },
  },
  compoundVariants: [],
  defaultVariants: {},
});

export default function SectionText(props: any) {
  let data = props;
  if (props?.data) {
    data = props.data;
  }
  const columnLayout = data.textColumnLayout?.toLowerCase();
  const RenderTag: any = `${data.layout}${columnLayout}`;

  return (
    <RenderTag is="x3d" customAttr="0.3">
      <div
        style={{ backgroundColor: data.bgColor?.color.hex }}
        className="w-full relative"
      >
        {data.addSpacer && (
          <Shape
            shape={data.spacerConfiguration?.customShape}
            color={data.bgColor}
            direction={data.spacerConfiguration?.spacerDirection}
            position={data.spacerConfiguration?.spacerPosition}
            icon={[
              data.spacerConfiguration?.addIconElement,
              data?.spacerConfiguration?.spacerIcon,
            ]}
            iconPosition={data.spacerConfiguration?.iconPosition}
            invert={false}
          />
        )}
        <div
          className={getSectionMarginClasses({
            useSectionMargins: data.useSectionMargins,
            useExtraTopMargins: data.useExtraSectionMargins,
            useExtraBottomMargins: data.useExtraBottomSectionMargins,
          })}
        >
          <div
            className={`container flex gap-12 relative mx-auto w-full flex-col ${SectionTextVariants(
              {
                textColumnLayout: data.textColumnLayout,
                textColumnDisplay: data.textColumnDisplay,
              }
            )} `}
          >
          {data.addIcon && (
            <Image
              className={`hidden md:block absolute m-auto left-0 bottom-0 md:h-[100px] md:w-[96px] lg:h-auto lg:w-auto z-10`}
              data={data.customIcon?.img}
            />
          )}

          <div
            className={`flex flex-col gap-4 md:gap-8 ${SectionTextVariants({
              layout: data.layout,
              textAlignment: data.textAlignment,
            })} `}
          >
            {data.addLabel && data.label && (
              <MultiPurposeParagraph
                type="label"
                data={data.label}
                color={data.titleColor?.color || data.textColor?.color}
              />
            )}
            {data.addTitle && (
              <div className="flex flex-col w-fit">
                <MultiPurposeTitle
                  type="default"
                  tag="h3"
                  data={data.title}
                  color={data.titleColor?.color || data.textColor?.color}
                  columns={data?.textColumnLayout}
                  layout={data?.textAlignment}
                />
                {data.addTitleUnderline && (
                  <>
                    {data.customUnderlineAsset?.img ? (
                      <CustomUnderline
                        shape={data.customUnderlineAsset?.img}
                        color={data.underlineColor?.color}
                      />
                    ) : (
                      <Underline color={data.underlineColor?.color} />
                    )}
                  </>
                )}
              </div>
            )}
            <div
              className={`${
                data.textColumnLayout !== "single"
                  ? `${styles.doubleColumn}`
                  : `${styles.singleColumn} `
              }`}
            >
              <MultiTextComponent
                data={data.text}
                color={data.textColor?.color}
                layout={data?.textAlignment}
                columns={data?.textColumnLayout}
              />
            </div>
          </div>
        </div>
        </div>
      </div>
    </RenderTag>
  );
}

export const fragment = `
    fragment TextFragment on SectionTextRecord{
      __typename
      id
      addTitle
      addLabel
      label
      title
      addTitleUnderline
      customUnderlineAsset {
        img {
          url
          width
          height
        }
      }
      underlineColor {
        color {
          hex
        }
        name
      }
	  addIcon
	  customIcon {
		img {
		  alt
		  url
		  width
		  height
		}
	  }
	  customIconColor{
		color{
		  hex
		}
	  }
      addSpacer
      
      spacerConfiguration {
        id
        spacerDirection
        spacerPosition
        customShape {
          img {
            alt
            url
            width
            height
          }
        }
        addIconElement
        spacerIcon {
          img {
            alt
            url
            width
            height
          }
        }
        iconPosition
      }
      text(markdown: true)
      layout
      textAlignment
      textColumnLayout
      textColumnDisplay
      useSectionMargins
      useExtraSectionMargins
	  useExtraBottomSectionMargins
      textColor{
        color{
          hex
        }
      }
      titleColor{
        color{
          hex
        }
      }
    bgColor{
      color{
        hex
      }
    }
  }
`;
